import { inject } from '@angular/core';
import { debounceTime, map, Observable, switchMap } from 'rxjs';
import { FiltersValues } from '../../../models';
import { capitalizeFirstLetter, DateFormats, formatDateToString, GameDetailsModel, mapCommonGameDto } from '@core';
import { parseFiltersData } from '../../../utils';
import { parseISO } from 'date-fns';
import { FiltersStorage, NearbyGamesService } from '@find-games/infra';
import { CommonGameDto, NearbyGamesListDto } from '@core-http';
import { GamesInSingleDayModel, GamesListQueryResult } from './games-list.query-result';

export class GamesListQueryHandler {
  private readonly nearbyGamesService: NearbyGamesService = inject(NearbyGamesService);
  private readonly filtersStorage: FiltersStorage = inject(FiltersStorage);

  getGames(showPrivate: boolean): Observable<GamesListQueryResult> {
    return this.filtersStorage.filters$.pipe(
      debounceTime(300),
      map((filters: FiltersValues) => parseFiltersData(filters)),
      switchMap((filters: string) => this.nearbyGamesService.getGamesList(filters, showPrivate)),
      map((dto: NearbyGamesListDto) => this.mapToQueryResult(dto))
    );
  }

  private mapToQueryResult(dto: NearbyGamesListDto): GamesListQueryResult {
    const groupedGames: Record<string, GameDetailsModel[]> = this.groupByDate(dto.games);

    const sortedDays: GamesInSingleDayModel[] = Object.keys(groupedGames)
      .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime())
      .map((date: string) => ({
        date: this.formatDate(date),
        games: groupedGames[date]
      }));

    return {
      day: sortedDays,
      ignoredLocationFilter: dto.ignoredLocationFilter
    };
  }

  private groupByDate(games: CommonGameDto[]): Record<string, GameDetailsModel[]> {
    return games.reduce(
      (acc: Record<string, GameDetailsModel[]>, game: CommonGameDto) => {
        const date: string = formatDateToString(game.gameDateStart, DateFormats.IsoDate);
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(mapCommonGameDto(game));
        return acc;
      },
      {} as Record<string, GameDetailsModel[]>
    );
  }

  private formatDate(dateString: string): string {
    const date: Date = parseISO(dateString);
    const formattedDate: string = formatDateToString(date, DateFormats.WeekdayFullDate);

    return capitalizeFirstLetter(formattedDate);
  }
}
